<!-- Shared: revision timeline + project-total summary. -->
<ng-template #coDetailBlock let-tl="tl" let-coVal="coVal" let-prev="prev" let-prevLbl="prevLbl" let-newTotal="newTotal" let-revLbl="revLbl" let-cur="cur">
	<div class="co-timeline mb-2">
		<div class="co-tl-item d-flex gap-2" *ngFor="let t of tl; let last = last"
			[class.co-tl-last]="last" [class.co-tl-current]="t.current" [class.co-tl-accepted]="t.status === 'accepted'">
			<div class="co-tl-rail d-flex flex-column align-items-center">
				<span class="co-tl-dot"><i class="wq wq-check" *ngIf="t.status === 'accepted'"></i></span>
				<span class="co-tl-line" *ngIf="!last"></span>
			</div>
			<div class="co-tl-body pb-3">
				<div class="d-flex align-items-center flex-wrap gap-2">
					<span class="co-tl-title">{{ t.title }}</span>
					<span class="badge" [ngClass]="{'badge-success': t.status === 'accepted', 'badge-warning': t.status === 'pending', 'badge-primary': t.status === 'declined', 'badge-secondary': t.status === 'draft'}">{{ t.status_label }}</span>
					<span class="qr-date qr-date-pending" *ngIf="t.valid_until">Valid until {{ t.valid_until | localizedDate }}</span>
				</div>
				<div class="co-tl-desc" *ngIf="t.description">{{ t.description }}</div>
			</div>
		</div>
	</div>

	<div class="co-sum-table">
		<div class="co-sum-row d-flex align-items-center justify-content-between px-3 py-3">
			<span>{{ prevLbl }} Project Total</span>
			<span class="font-weight-bold">{{ prev | currency:cur }}</span>
		</div>
		<div class="co-sum-row d-flex align-items-center justify-content-between px-3 py-3">
			<span>Change Order {{ revLbl }}</span>
			<span class="font-weight-bold" [class.text-success-solid]="coVal > 0" [class.text-danger-solid]="coVal < 0">{{ signedMoney(coVal, cur) }}</span>
		</div>
		<div class="co-sum-row co-sum-total d-flex align-items-center justify-content-between px-3 py-3">
			<span class="font-weight-bold">New Project Total <span class="small text-muted d-block">if accepted</span></span>
			<span class="font-weight-bold text-success-solid">{{ newTotal | currency:cur }}</span>
		</div>
	</div>
</ng-template>

<portal-modal [size]="modalSize" [modalTitle]="modalTitle" [subtitle]="subtitle" [footer]="isPending()">

	<ng-container *ngIf="order">
		<div class="qr-status mb-3">
			<span class="badge" [ngClass]="badgeClass(order.status)">{{ badgeLabel(order.status) }}</span>
			<span class="qr-date" [ngClass]="dateLabel().cls">{{ dateLabel().text }}</span>
		</div>

		<!-- View actions: always available (independent of the decision state). -->
		<div class="d-flex gap-2 mb-3">
			<portal-button *ngIf="order.show_co_breakdown" class="flex-fill" (click)="openDetailsPage()" [disabled]="!order.can_view_change_order" [title]="order.can_view_change_order ? null : 'No change order page available'">View CO Details</portal-button>
			<portal-button class="flex-fill" (click)="openProposal()" [disabled]="!order.can_view_proposal" [title]="order.can_view_proposal ? null : 'No proposal published for this change order'">View Proposal Changes</portal-button>
		</div>

		<portal-loader *ngIf="loading"></portal-loader>
		<div class="sec-state portal-error" *ngIf="error">{{ error }}</div>

		<ng-container *ngIf="!loading && !error && detail">
			<ng-container *ngTemplateOutlet="coDetailBlock; context: {
				tl: detail.timeline, coVal: order.change_value,
				prev: (order.status === 'accepted' ? order.previous_project_total : order.current_project_total),
				prevLbl: (order.status === 'accepted' ? 'Previous' : 'Current'),
				newTotal: order.new_project_total, revLbl: revLabel, cur: order.currency_id }"></ng-container>

			<ng-container *ngIf="order.show_co_breakdown">
				<div class="font-weight-bold mt-3 mb-2">New Project Total Breakdown</div>

				<p class="text-muted m-0" *ngIf="!detailGroups.length">No line-level changes.</p>

				<div class="co-changes-scroll" *ngIf="detailGroups.length">
					<table class="co-changes-table">
						<thead>
							<tr>
								<th>Item</th>
								<th *ngIf="detail.show_system">System</th>
								<th *ngIf="detail.show_area">Area</th>
								<th class="co-num text-center">{{ detail.tax_term || 'VAT' }} rate</th>
								<th class="co-num text-center">Unit Price</th>
								<th class="co-num text-center">Quantity</th>
								<th class="co-num">Total</th>
							</tr>
						</thead>
						<tbody *ngFor="let g of detailGroups">
							<tr class="co-changes-group" [ngClass]="g.kind">
								<th [attr.colspan]="itemColspan">{{ g.label }}</th>
								<th class="co-num text-center">{{ groupQty(g) }}</th>
								<th class="co-num">{{ groupTotal(g) }}</th>
							</tr>
							<tr *ngFor="let l of g.lines">
								<td>
									<ng-container *ngIf="detail.product_details && l.item_lines?.length; else plainItem">
										<div class="font-weight-bold">{{ l.item_lines[0] }}</div>
										<div class="text-muted co-changes-sub" *ngIf="l.item_lines[1]">{{ l.item_lines[1] }}</div>
										<div class="text-muted co-changes-code" *ngIf="l.item_lines[2]">{{ l.item_lines[2] }}</div>
									</ng-container>
									<ng-template #plainItem>{{ l.item }}</ng-template>
								</td>
								<td *ngIf="detail.show_system" class="text-muted">{{ l.system }}</td>
								<td *ngIf="detail.show_area" class="text-muted">{{ l.area }}</td>
								<td class="co-num text-muted text-center">{{ l.tax_rate }}%</td>
								<td class="co-num text-center">{{ l.unit_price | currency:detail.currency }}</td>
								<td class="co-num text-muted text-center">{{ lineQty(l, g) }}</td>
								<td class="co-num font-weight-bold">{{ lineTotal(l, g) }}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</ng-container>
		</ng-container>
	</ng-container>

	<!-- Actions: comment button stays compact, the rest stretch to fill the row. -->
	<div portal-modal-footer class="d-flex align-items-center gap-2 flex-fill">
		<portal-button icon="wq wq-notes" [title]="preview.ready ? preview.blockedReason : (order?.can_comment ? 'Add a comment' : 'No proposal published for this change order')" aria-label="Add a comment" (click)="onComment()" [disabled]="preview.ready || !order?.can_comment"></portal-button>
		<portal-button class="flex-fill" *ngIf="canConfirm()" (click)="onConfirm()" [disabled]="preview.ready" [title]="preview.ready ? preview.blockedReason : null">To be confirmed</portal-button>
		<portal-button class="flex-fill" (click)="onReject()" [disabled]="preview.ready" [title]="preview.ready ? preview.blockedReason : null">Reject</portal-button>
		<portal-button class="flex-fill" (click)="onAccept()" [disabled]="preview.ready || !order?.can_accept" [title]="preview.ready ? preview.blockedReason : (order?.can_accept ? null : 'No proposal published for this change order')">Accept</portal-button>
	</div>
</portal-modal>
